// Post-acceptance "pay the IRS now" deep link, keyed by the payment method saved
// in the Pay step (Payment.paymentKind - see backend PaymentRequest: efw/eftps/card).
//
// Only efw (electronic funds withdrawal / direct debit) is paid through the e-file
// itself - the IRS auto-debits the bank account on record once the return is
// processed, so it needs no further filer action. eftps and card are paid by the
// filer out-of-band, on a separate IRS system - we only guide them to the right
// place.
//
// Verified against IRS i2290 (Rev. 7-2026) "How To Pay the Tax": EFTPS payments
// are made at EFTPS.gov (enrollment required); credit/debit card payments go
// through IRS.gov/PayByCard (convenience fee applies).
export function payNowLink(paymentKind) {
  switch (paymentKind) {
    case "eftps":
      return { href: "https://www.eftps.gov", label: "Pay via EFTPS" };
    case "card":
      return { href: "https://www.irs.gov/paybycard", label: "Pay by card" };
    default:
      return null;
  }
}

// Full post-payment guidance, shown on the confirmation screen alongside the
// service-fee receipt. Same source and same conclusions as payNowLink above, but
// spelled out — the filer has just paid US, and without this the natural reading of
// "payment successful" is that the tax is settled too. It isn't, for eftps and card.
//
// Kept beside payNowLink deliberately: the scheduler emails the SAME guidance when the
// IRS accepts the return (form720-scheduler EmailService.payGuidance2290). Screen and
// email must agree, so change both together.
//
// No "check" case — check/money order was removed as a 2290 method entirely (backend
// data/V2__remove_check_payment_method.sql): it has no IRS2290.xsd representation, and
// Business Rule F2290-068-01 requires a balance-due return to carry EFTPS, credit/debit
// card, or an IRSPayment record.
//
// Callers MUST gate on there being a balance due — a return reporting only suspended
// vehicles owes nothing, and telling that filer a payment is outstanding is simply wrong.
export function payInstructions(paymentKind, { amountLabel } = {}) {
  const amount = amountLabel ? ` ${amountLabel}` : "";
  switch (paymentKind) {
    case "efw":
      return {
        settled: true,
        heading: "Your tax payment is scheduled",
        body: `The IRS will withdraw${amount} from the bank account you authorized on your return once it is processed. No further action is needed from you.`,
      };
    case "eftps":
      return {
        settled: false,
        heading: `Your IRS tax payment${amount} is still due`,
        body:
          "This is the tax itself, not our filing fee. Filing does not send the money — sign in at EFTPS.gov and pay it yourself. " +
          "If you haven't enrolled in EFTPS, you must enroll before you can pay: EFTPS.gov or 800-555-4477, available 24 hours a day, 7 days a week. " +
          "To be on time, submit your payment by 8:00 p.m. Eastern time the day before it is due.",
      };
    case "card":
      return {
        settled: false,
        heading: `Your IRS tax payment${amount} is still due`,
        body:
          "This is the tax itself, not our filing fee. Card payments go directly to the IRS, not through us — " +
          "go to IRS.gov/PayByCard to pay. The card processor charges a convenience fee.",
      };
    default:
      return null;
  }
}
